import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Briefcase,
  UserCheck,
  UserPlus,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  GraduationCap,
  Building2,
  Factory,
  Package,
  Heart,
  UtensilsCrossed,
  Code
} from 'lucide-react';

const ManpowerServices = () => {
  const services = [
    {
      icon: Code,
      title: "IT Staffing",
      description: "Skilled IT professionals for development, testing, and support roles"
    },
    {
      icon: Users,
      title: "Non-IT Staffing",
      description: "Qualified professionals across all non-technical domains"
    },
    {
      icon: Briefcase,
      title: "Contract Staffing",
      description: "Flexible contract-based hiring for short-term projects"
    },
    {
      icon: UserCheck,
      title: "Permanent Placement",
      description: "Long-term hiring solutions with comprehensive screening"
    },
    {
      icon: TrendingUp,
      title: "Bulk Hiring",
      description: "Large-scale recruitment for rapid team expansion"
    },
    {
      icon: UserPlus,
      title: "Remote Workforce",
      description: "Work-from-home professionals for flexible operations"
    }
  ];

  const industries = [
    { icon: Code, name: "IT / Software" },
    { icon: Building2, name: "Construction" },
    { icon: Factory, name: "Manufacturing" },
    { icon: Package, name: "Logistics" },
    { icon: GraduationCap, name: "Education" },
    { icon: Heart, name: "Healthcare" },
    { icon: UtensilsCrossed, name: "Hospitality" },
    { icon: Users, name: "Corporate" }
  ];

  const process = [
    { step: "01", title: "Requirement Gathering", desc: "Understanding your exact hiring needs and job specifications" },
    { step: "02", title: "Candidate Screening", desc: "Thorough screening from our verified talent pool" },
    { step: "03", title: "Interview Coordination", desc: "Arranging interviews with shortlisted candidates" },
    { step: "04", title: "Joining Support", desc: "Onboarding assistance and documentation support" }
  ];

  const benefits = [
    "Access to pre-verified talent pool",
    "Fast hiring - as quick as 7 days",
    "Background verification included",
    "Replacement guarantee",
    "Competitive pricing",
    "PAN India reach"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-blue-100 text-blue-700 mb-4">Manpower Services</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Find the Right{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Talent for Your Business
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Professional staffing solutions for IT and Non-IT roles. From contract hiring 
            to permanent placements, we connect you with skilled professionals.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg">
              Hire Talent Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Staffing Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive recruitment solutions for all your hiring needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Providing talent across diverse sectors
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-lg transition-all duration-300 border-2 hover:border-blue-200 bg-white">
                <industry.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{industry.name}</h3>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hiring Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our Hiring Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Simple and efficient recruitment workflow
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <Card className="p-8 h-full border-2 hover:border-blue-200 transition-all duration-300">
                  <div className="text-5xl font-bold text-blue-100 mb-4">{item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </Card>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-blue-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Our Staffing Services?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We combine extensive networks, rigorous screening, and industry expertise 
                to deliver the best talent for your organization.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Card className="p-8 text-center bg-white border-2 border-blue-100">
                <div className="text-4xl font-bold text-blue-600 mb-2">10K+</div>
                <div className="text-gray-600">Candidates Database</div>
              </Card>
              <Card className="p-8 text-center bg-white border-2 border-blue-100">
                <div className="text-4xl font-bold text-blue-600 mb-2">7 Days</div>
                <div className="text-gray-600">Average Hiring Time</div>
              </Card>
              <Card className="p-8 text-center bg-white border-2 border-blue-100">
                <div className="text-4xl font-bold text-blue-600 mb-2">95%</div>
                <div className="text-gray-600">Client Satisfaction</div>
              </Card>
              <Card className="p-8 text-center bg-white border-2 border-blue-100">
                <div className="text-4xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-600">Successful Placements</div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Need to Hire Skilled Professionals?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Share your requirements and we'll find the perfect match for you
          </p>
          <Link to="/contact">
            <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg">
              Post Your Requirement
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ManpowerServices;
